// One rule, one place: inside ONE bracket a game may only be fed by a game in
// a LOWER bracket_round. The slot dropdown filters by it, set_bracket_slot
// refuses by it, the integrity audit's eighth check reports it, and a dragged
// game is validated by it. Do not write a fourth copy.
//
// Moving a game changes bracket_round and bracket_slot and NOTHING else. There
// are no x/y columns: the canvas derives every position from the round and the
// order within it, which is what keeps the connectors meeting the games.

export type MoveGame = {
  id: string;
  game_number?: number | null;
  bracket_id?: string | null;
  bracket_round?: number | null;
  bracket_slot?: number | null;
  bracket_label?: string | null;
  source_a_type?: string | null;
  source_a_ref?: any;
  source_b_type?: string | null;
  source_b_ref?: any;
};

export const roundOfGame = (g: MoveGame | null | undefined) =>
  Number(g?.bracket_round ?? 0) || 0;

export const gameWords = (g: MoveGame | null | undefined) =>
  g?.game_number ? `Game ${g.game_number}` : (g?.bracket_label || "that game");

/** The ids of the games feeding this one, read from the stored sources only. */
export function feederIdsOf(g: MoveGame): string[] {
  const out: string[] = [];
  for (const side of ["a", "b"] as const) {
    const type = (g as any)[`source_${side}_type`];
    const ref = (g as any)[`source_${side}_ref`];
    if ((type === "winner_of" || type === "loser_of") && ref?.game_id) out.push(String(ref.game_id));
  }
  return out;
}

/**
 * The primitive. `null` when the feed is fine, otherwise how it is wrong.
 * Across brackets a round number means nothing (a consolation round 1 runs on
 * losers of a Winners round 1), so this only ever judges one bracket.
 */
export function feedRoundFault(
  feederRound: number,
  gameRound: number,
  sameBracket: boolean,
): "same" | "later" | null {
  if (!sameBracket || !feederRound || !gameRound) return null;
  if (feederRound === gameRound) return "same";
  if (feederRound > gameRound) return "later";
  return null;
}

const listWords = (names: string[]) =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** Why a nudge inside a round has nowhere to go. One sentence, three callers. */
export function endOfRoundWords(g: MoveGame, dir: "up" | "down"): string {
  return `${gameWords(g)} is already ${dir === "up" ? "first" : "last"} in round `
    + `${roundOfGame(g) || 1}.`;
}

export type MoveUpdate = { id: string; bracket_round: number; bracket_slot: number };

export type MovePlan =
  | { ok: false; message: string }
  | {
    ok: true;
    updates: MoveUpdate[];
    from: { bracket_round: number; bracket_slot: number };
    to: { bracket_round: number; bracket_slot: number };
    message: string;
  };

/** A slot parked at 99 means "always last" and is never folded into a sequence. */
export const PARKED_SLOT = 99;

/** The games of one round inside one bracket, top to bottom, as the canvas draws them. */
function roundList(mine: MoveGame[], round: number, excludeId?: string): MoveGame[] {
  return mine
    .filter((g) => roundOfGame(g) === round && String(g.id) !== String(excludeId ?? ""))
    .sort((a, b) =>
      (Number(a.bracket_slot ?? 0) - Number(b.bracket_slot ?? 0))
      || (Number(a.game_number ?? 0) - Number(b.game_number ?? 0)));
}

/**
 * Inside one round, games must be drawn in the order their feeders are drawn:
 * if a game is fed from higher up the previous round than its neighbour, it
 * sits above that neighbour. Otherwise the connectors cross.
 *
 * A game with no feeders in this bracket — a first round game, an unwired slot,
 * or one fed from another bracket entirely — has no constraint and may sit
 * anywhere, because a position in another bracket says nothing about this one.
 */
function crossingFault(
  mine: MoveGame[],
  moving: MoveGame,
  toRound: number,
  toIndex: number,
): string | null {
  // Where every game is drawn today, so a feeder's position can be read off.
  const pos = new Map<string, number>();
  for (const r of new Set(mine.map((g) => roundOfGame(g)))) {
    roundList(mine, r).forEach((g, i) => pos.set(String(g.id), i));
  }
  const byId = new Map(mine.map((g) => [String(g.id), g]));
  const feedersOf = (g: MoveGame) =>
    feederIdsOf(g).map((id) => byId.get(String(id))).filter(Boolean) as MoveGame[];
  /** The highest-drawn feeder of a game, or null when nothing in this bracket feeds it. */
  const anchor = (g: MoveGame): number | null => {
    const ps = feedersOf(g).map((f) => pos.get(String(f.id))).filter((p) => p !== undefined) as number[];
    return ps.length ? Math.min(...ps) : null;
  };

  const rest = roundList(mine, toRound, String(moving.id));
  const at = Math.max(0, Math.min(toIndex, rest.length));
  const ordered = [...rest.slice(0, at), moving, ...rest.slice(at)];
  const mineAnchor = anchor(moving);
  if (mineAnchor === null) return null;

  const words = (g: MoveGame) => listWords(feedersOf(g).map(gameWords));
  for (let i = 0; i < ordered.length; i++) {
    const other = ordered[i];
    if (String(other.id) === String(moving.id)) continue;
    const a = anchor(other);
    if (a === null) continue;
    const above = i < at;   // this game would sit above the moving one
    if (above && a > mineAnchor) {
      return `${gameWords(moving)} takes its teams from ${words(moving)}, which sit above `
        + `${words(other)}. Putting it below ${gameWords(other)} would make the lines cross.`;
    }
    if (!above && a < mineAnchor) {
      return `${gameWords(moving)} takes its teams from ${words(moving)}, which sit below `
        + `${words(other)}. Putting it above ${gameWords(other)} would make the lines cross.`;
    }
  }
  return null;
}

/**
 * Why this game may not sit in that round, in the director's words, or null
 * when it may. Read while dragging so an invalid column looks invalid BEFORE
 * he lets go, and again on the server before anything is written.
 *
 * Pass `toIndex` — the position it would take inside that round — and the
 * crossing rule is judged too. Without it only the round is judged.
 */
export function moveBlocker(
  all: MoveGame[],
  gameId: string,
  toRound: number,
  toIndex?: number | null,
): string | null {
  const moving = (all ?? []).find((g) => String(g.id) === String(gameId));
  if (!moving) return "That game is not in this bracket.";
  if (!(Number(toRound) > 0)) return "A bracket round starts at 1.";
  const round = Number(toRound);
  const bracketId = String(moving.bracket_id ?? "");
  const mine = (all ?? []).filter((g) => String(g.bracket_id ?? "") === bracketId);
  const byId = new Map(mine.map((g) => [String(g.id), g]));

  // ---- its own feeds: they must stay in an earlier round -------------------
  const feeders = feederIdsOf(moving)
    .map((id) => byId.get(String(id)))
    .filter(Boolean) as MoveGame[];
  const blockingFeeders = feeders.filter(
    (f) => feedRoundFault(roundOfGame(f), round, true) !== null,
  );
  if (blockingFeeders.length) {
    const rounds = [...new Set(blockingFeeders.map((f) => roundOfGame(f)))].sort((a, b) => a - b);
    return `${gameWords(moving)} is fed by ${listWords(blockingFeeders.map(gameWords))}, which `
      + `${blockingFeeders.length === 1 ? "is" : "are"} in round ${rounds.join(" and ")}. `
      + `Moving it to round ${round} would put it alongside the games that decide who is in it.`;
  }

  // ---- the games it feeds: they must stay in a later round -----------------
  const children = mine.filter((g) =>
    String(g.id) !== String(moving.id) && feederIdsOf(g).includes(String(moving.id)));
  const blockingChildren = children.filter(
    (c) => feedRoundFault(round, roundOfGame(c), true) !== null,
  );
  if (blockingChildren.length) {
    const rounds = [...new Set(blockingChildren.map((c) => roundOfGame(c)))].sort((a, b) => a - b);
    const later = blockingChildren.some((c) => roundOfGame(c) < round);
    return `${listWords(blockingChildren.map(gameWords))} `
      + `${blockingChildren.length === 1 ? "takes its teams" : "take their teams"} from `
      + `${gameWords(moving)}, and ${blockingChildren.length === 1 ? "sits" : "sit"} in round `
      + `${rounds.join(" and ")}. Moving ${gameWords(moving)} to round ${round} would put it `
      + `${later ? "after" : "alongside"} the game${blockingChildren.length === 1 ? "" : "s"} it decides.`;
  }

  // ---- the order inside the round: connectors must not cross ---------------
  if (toIndex !== undefined && toIndex !== null) {
    const cross = crossingFault(mine, moving, round, Number(toIndex) || 0);
    if (cross) return cross;
  }
  return null;
}


/**
 * Where a game would land, and whether it may.
 *
 * `toIndex` is the position among the games already in the target round, in
 * the order the canvas draws them (0 = top). Dropping between two games does
 * NOT insert a fractional slot: the whole target round — and the round the
 * game left — is renumbered 0..n in display order, the same numbering the
 * generator writes, so one bracket never carries two conventions. A slot
 * parked at 99 (the 3rd place game) is left exactly where it is: it means
 * "always last", and folding it into the sequence would turn 99 into 2.
 *
 * The position an operator reads is 1-based; the stored slot is not.
 */
export function planBracketMove(
  all: MoveGame[],
  args: { gameId: string; toRound: number; toIndex: number },
): MovePlan {
  const moving = (all ?? []).find((g) => String(g.id) === String(args.gameId));
  if (!moving) return { ok: false, message: "That game is not in this bracket." };
  const toRound = Number(args.toRound) || 0;
  const blocked = moveBlocker(all, args.gameId, toRound, Number(args.toIndex) || 0);
  if (blocked) return { ok: false, message: blocked };

  const bracketId = String(moving.bracket_id ?? "");
  const mine = (all ?? []).filter((g) => String(g.bracket_id ?? "") === bracketId);

  // ---- renumber the rounds it left and the round it joins ------------------
  const fromRound = roundOfGame(moving);
  const fromSlot = Number(moving.bracket_slot ?? 0) || 0;
  const inRound = (round: number) => roundList(mine, round, String(moving.id));

  const target = inRound(toRound);
  const index = Math.max(0, Math.min(Number(args.toIndex) || 0, target.length));
  const ordered = [...target.slice(0, index), moving, ...target.slice(index)];

  const updates: MoveUpdate[] = [];
  /** Number a round from 0, stepping over anything parked at 99. */
  const renumber = (list: MoveGame[], round: number) => {
    let next = 0;
    for (const g of list) {
      const isMoving = String(g.id) === String(moving.id);
      if (!isMoving && Number(g.bracket_slot ?? 0) === PARKED_SLOT) continue;
      const slot = next++;
      if (isMoving || roundOfGame(g) !== round || Number(g.bracket_slot ?? 0) !== slot) {
        updates.push({ id: String(g.id), bracket_round: round, bracket_slot: slot });
      }
    }
  };
  renumber(ordered, toRound);
  if (fromRound && fromRound !== toRound) renumber(inRound(fromRound), fromRound);

  const movedTo = updates.find((u) => u.id === String(moving.id))!;
  if (fromRound === toRound && fromSlot === movedTo.bracket_slot && updates.length === 1) {
    return {
      ok: false,
      message: `${gameWords(moving)} is already in round ${toRound}, position ${fromSlot + 1}.`,
    };
  }

  const shown = movedTo.bracket_slot + 1;
  return {
    ok: true,
    updates,
    from: { bracket_round: fromRound, bracket_slot: fromSlot },
    to: { bracket_round: movedTo.bracket_round, bracket_slot: movedTo.bracket_slot },
    message: fromRound === toRound
      ? `${gameWords(moving)} moved to position ${shown} of round ${toRound}.`
      : `${gameWords(moving)} moved to round ${toRound}, position ${shown}.`,
  };
}

